import { sprintf } from 'sprintf-js';

import { apiSurveyInstance, apiInstance } from 'api/utils';
import { SearchParamsType } from 'api/types';

const URLS = {
  CSV: '/org/%s/reports/tables/%s/grid/csv/',
  TABLES: '/org/%s/reports/tables/',
  FILES: '/org/%s/projects/reports/',
  FILE: '/org/%s/projects/reports/%s/',
  REPORT: '/org/%s/surveys/%s/report/',
  INDIVIDUAL_REPORTS: '/org/%s/surveys/%s/individual-report/',
  INDIVIDUAL_ANSWERS: '/org/%s/surveys/%s/individual-question/?result_id=%s',
  DEFAULT_REPORT: '/org/%s/reports/report-diagrams/?project_uuid=%s',
  SHARED_LINKS: '/org/%s/reports/surveys/%s/shared-report-links/',
  SHARED_LINK: '/org/%s/reports/surveys/%s/shared-report-links/%s/',
  SHARED_REPORT: '/org/%s/reports/shared-report-link-access/%s/',
  SHARED_REPORT_FILES:
    '/org/%s/reports/shared-report-link-access/%s/file/download/',
  SHARED_REPORT_ID: '/org/%s/reports/surveys/%s/shared-report-links/%s/',
};

export default {
  getTables: ({
    organizationCodename,
    projectUuid,
    params,
  }: {
    organizationCodename: string;
    projectUuid: string;
    params?: SearchParamsType;
  }) =>
    apiSurveyInstance.request({
      method: 'GET',
      url: sprintf(URLS.TABLES, organizationCodename),
      params: {
        ...params,
        project_uuid: projectUuid,
      },
    }),
  getReportCsv: ({
    organizationCodename,
    reportId,
    params,
  }: {
    organizationCodename: string;
    reportId: string | number;
    params?: SearchParamsType;
  }) =>
    apiSurveyInstance.request({
      method: 'GET',
      url: sprintf(URLS.CSV, organizationCodename, reportId),
      params,
    }),
  uploadReportFile: ({
    organizationCodename,
    params,
    html,
    project_uuid,
  }: {
    organizationCodename: string;
    params?: SearchParamsType;
    html: string;
    project_uuid: string;
  }) =>
    apiInstance.request({
      method: 'POST',
      url: sprintf(URLS.FILES, organizationCodename),
      params,
      data: {
        html,
        project_uuid,
      },
    }),
  getReportFiles: ({
    organizationCodename,
    params,
  }: {
    organizationCodename: string;
    params?: {
      project_uuid: string;
      limit?: SearchParamsType['limit'];
      offset?: SearchParamsType['offset'];
    };
  }) =>
    apiInstance.request({
      method: 'GET',
      url: sprintf(URLS.FILES, organizationCodename),
      params,
    }),
  removeReportFile: ({
    organizationCodename,
    fileID,
  }: {
    organizationCodename: string;
    fileID: number;
  }) =>
    apiInstance.request({
      method: 'DELETE',
      url: sprintf(URLS.FILE, organizationCodename, fileID),
    }),
  getReport: ({
    organizationCodename,
    surveyUuid,
  }: {
    organizationCodename: string;
    surveyUuid: any;
  }) =>
    apiSurveyInstance.request({
      method: 'GET',
      url: sprintf(URLS.REPORT, organizationCodename, surveyUuid),
    }),
  getAllReports: ({
    organizationCodename,
    surveyUuid,
  }: {
    organizationCodename: string;
    surveyUuid: any;
  }) =>
    apiSurveyInstance.request({
      method: 'GET',
      url: sprintf(URLS.INDIVIDUAL_REPORTS, organizationCodename, surveyUuid),
    }),
  getSingleReport: ({
    organizationCodename,
    surveyUuid,
    reportId,
  }: {
    organizationCodename: string;
    surveyUuid: any;
    reportId: number;
  }) =>
    apiSurveyInstance.request({
      method: 'GET',
      url: sprintf(
        URLS.INDIVIDUAL_ANSWERS,
        organizationCodename,
        surveyUuid,
        reportId
      ),
    }),
  getDefaultReport: ({
    organizationCodename,
    projectUuid,
  }: {
    organizationCodename: string;
    projectUuid: any;
  }) =>
    apiSurveyInstance.request({
      method: 'GET',
      url: sprintf(URLS.DEFAULT_REPORT, organizationCodename, projectUuid),
    }),

  getSharingLinks: ({
    organizationCodename,
    surveyId,
  }: {
    organizationCodename: string;
    surveyId: any;
  }) =>
    apiSurveyInstance.request({
      method: 'GET',
      url: sprintf(URLS.SHARED_LINKS, organizationCodename, surveyId),
    }),

  createSharingLink: ({
    organizationCodename,
    surveyId,
    data,
  }: {
    organizationCodename: string;
    surveyId: any;
    data: any;
  }) =>
    apiSurveyInstance.request({
      method: 'POST',
      url: sprintf(URLS.SHARED_LINKS, organizationCodename, surveyId),
      data: data,
    }),
  deleteSharingLink: ({
    organizationCodename,
    surveyId,
    linkId,
  }: {
    organizationCodename: string;
    surveyId: any;
    linkId: number;
  }) =>
    apiSurveyInstance.request({
      method: 'DELETE',
      url: sprintf(URLS.SHARED_LINK, organizationCodename, surveyId, linkId),
    }),
  editSharingLink: ({
    organizationCodename,
    surveyId,
    linkId,
    data,
  }: {
    organizationCodename: string;
    surveyId: any;
    linkId: number;
    data: any;
  }) =>
    apiSurveyInstance.request({
      method: 'PUT',
      url: sprintf(URLS.SHARED_LINK, organizationCodename, surveyId, linkId),
      data: data,
    }),
  toggleStatusSharingLink: ({
    organizationCodename,
    surveyId,
    linkId,
    isDisabled,
  }: {
    organizationCodename: string;
    surveyId: any;
    linkId: number;
    isDisabled: boolean;
  }) =>
    apiSurveyInstance.request({
      method: 'PATCH',
      url: sprintf(
        URLS.SHARED_REPORT_ID,
        organizationCodename,
        surveyId,
        linkId
      ),
      data: {
        is_disabled: isDisabled,
      },
    }),
  getSharedReport: ({
    organizationCodename,
    linkId,
    password,
  }: {
    organizationCodename: string;
    linkId: number;
    password?: string;
  }) =>
    apiSurveyInstance.request({
      method: 'POST',
      url: sprintf(URLS.SHARED_REPORT, organizationCodename, linkId),
      data: { password: password },
    }),
  getSharedReportFile: ({
    organizationCodename,
    linkId,
    fileId,
  }: {
    organizationCodename: string;
    linkId: string;
    fileId: string;
  }) =>
    apiSurveyInstance.request({
      method: 'POST',
      url: sprintf(URLS.SHARED_REPORT_FILES, organizationCodename, linkId),
      data: { path: fileId },
    }),
};
